import React from "react";

export interface GroupByObject<T, TGroup> {
    selector: (item: T) => TGroup,
    component?: (props: { item: TGroup }) => JSX.Element
}

interface IDropdownListProps<T, TGroup> {
    isOpen: boolean;
    items: T[];
    groupBy?: GroupByObject<T, TGroup>;
    itemComponent: (item: T) => React.ReactNode;
    onSelect: (item: T) => void;
    ulStyle?: React.CSSProperties;
}

interface CommonProps<T> {
    itemComponent?: { (props: { item: T }): JSX.Element };
    textField: ((item: T) => string);
}
interface IDropdownListState {
    selectedIndex: number;
}

export function getComponent<T, TGroup>(props: CommonProps<T>, item: T | null) {
    if (item == null) return null;
    if (props.itemComponent) {
        let Comp = props.itemComponent;
        return <Comp item={item} />
    }
    else return <span> {props.textField(item)}</span>
}

function SimpleListComponent<T>(props: {
    items: T[];
    itemComponent: (item: T) => React.ReactNode;
    onSelect: (item: T) => void,
    selectedIndex: number,
}) {
    const ul = React.useRef<HTMLUListElement>(null)
    React.useEffect(() => {
        const lis = ul.current?.querySelector("li.active");
        lis?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [props.selectedIndex]);
    return <ul ref={ul}>
        {props.items.map((d, i) => (
            <li key={i}
                className={`bncombo-item  ${i === props.selectedIndex ? "active" : ""}`}
                onClick={() => props.onSelect(d)}>
                <div className="bncombo-item-content">{props.itemComponent(d)}</div>
            </li >
        ))}
    </ul>
}

function GroupedListComponent<T, TGroup>(props: {
    items: T[];
    groupBy: GroupByObject<T, TGroup>;
    itemComponent: (item: T) => React.ReactNode;
    onSelect: (item: T) => void,
    selectedIndex: number,
}) {
    const { groupBy, items, onSelect, selectedIndex, itemComponent } = props;

    const groupMethod = (data: T[], selector: (item: T) => TGroup) => {
        let result: { key: TGroup, group: T[] }[] = [];
        for (const item of data) {
            const key = selector(item);
            const index = result.findIndex(x => x.key === key);
            if (index !== -1)
                result[index].group.push(item)
            else
                result.push({ key, group: [item] })
        }

        return result;
    }

    const selectedItem = items[selectedIndex];

    return <div>
        {groupMethod(items, groupBy.selector)
            .map((x, i) => {
                const GroupItemComponent = groupBy.component;
                return (<div key={i}>
                    <div className="bncombo-item-group">
                        {GroupItemComponent && <GroupItemComponent item={x.key} /> || x.key}
                    </div>
                    <SimpleListComponent
                        selectedIndex={x.group.indexOf(selectedItem)}
                        items={x.group}
                        itemComponent={itemComponent}
                        onSelect={onSelect}
                    />
                </div>)
            })
        }
    </div>
}

export class DropdownList<T, TGroup> extends React.Component<IDropdownListProps<T, TGroup>, IDropdownListState>
{
    constructor(props: IDropdownListProps<T, TGroup>) {
        super(props);
        this.state = {
            selectedIndex: -1,
        }
    }

    public moveUp = () => {
        let { selectedIndex } = this.state;
        if (selectedIndex === 0) return;
        selectedIndex -= 1;
        this.setState({ selectedIndex });
    }

    public moveDown = () => {
        let { selectedIndex } = this.state;
        if (selectedIndex + 1 >= this.props.items.length)
            selectedIndex = this.props.items.length - 1;
        else
            selectedIndex += 1;
        this.setState({ selectedIndex });
    }

    public resetSelection = () => this.setState({ selectedIndex: -1 });

    public selectIndex = (selectedIndex: number) => {
        if (this.props.items.length === 0) return;
        if (selectedIndex >= this.props.items.length)
            selectedIndex = this.props.items.length - 1;
        if (selectedIndex < 0)
            selectedIndex = 0;
        if (selectedIndex != this.state.selectedIndex)
            this.setState({ selectedIndex });
    }

    public select = () => {
        const { onSelect, items } = this.props;
        if (items.length > 0)
            onSelect(items[this.state.selectedIndex]);
    }


    render() {
        const { isOpen, items, itemComponent, onSelect, groupBy } = this.props;
        const { selectedIndex } = this.state;

        if (!isOpen) return null;
        return (items.length > 0 && <div className="bncombo-menu" style={this.props.ulStyle}>
            {groupBy != null
                ? <GroupedListComponent
                    itemComponent={itemComponent}
                    items={items} groupBy={groupBy}
                    selectedIndex={selectedIndex}
                    onSelect={onSelect}
                />
                : <SimpleListComponent
                    itemComponent={itemComponent}
                    items={items}
                    selectedIndex={selectedIndex}
                    onSelect={onSelect}
                />
            }
        </div >);
    }
}
